import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Star, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import MultiImageUpload from "@/components/MultiImageUpload";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const SubmitDiveSiteReport = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [diveSites, setDiveSites] = useState([]);
  const [marineLifeSuggestions, setMarineLifeSuggestions] = useState([]);
  const [formData, setFormData] = useState({
    dive_site_id: "",
    rating: 5,
    marine_life_observations: "",
    image_urls: []
  });
  const [loading, setLoading] = useState(false);

  // Suppress ResizeObserver error
  useEffect(() => {
    const resizeObserverErr = window.console.error;
    window.console.error = (...args) => {
      if (args[0]?.includes?.('ResizeObserver')) return;
      resizeObserverErr(...args);
    };

    // Also suppress the actual error
    window.addEventListener('error', (e) => {
      if (e.message.includes('ResizeObserver')) {
        e.stopImmediatePropagation();
        return false;
      }
    });
  }, []);

  useEffect(() => {
    // Check if user is logged in
    const currentUser = JSON.parse(localStorage.getItem("user") || "null");
    if (!currentUser) {
      toast.error("Please login to submit a dive site report");
      navigate('/account');
      return;
    }
    setUser(currentUser);

    // Fetch dive sites
    const fetchDiveSites = async () => {
      try {
        const response = await axios.get(`${API}/map/dive-sites`);
        setDiveSites(response.data);
      } catch (error) {
        console.error("Error loading dive sites:", error);
        toast.error("Failed to load dive sites");
      }
    };
    fetchDiveSites();
  }, [navigate]);

  useEffect(() => {
    // Fetch marine life suggestions when dive site is selected
    const fetchSuggestions = async () => {
      if (formData.dive_site_id) {
        try {
          const response = await axios.get(`${API}/dive-sites/${formData.dive_site_id}/marine-life-suggestions`);
          setMarineLifeSuggestions(response.data.suggestions || []);
        } catch (error) {
          console.error("Error loading suggestions:", error);
        }
      }
    };
    fetchSuggestions();
  }, [formData.dive_site_id]);

  const handleRatingChange = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const addMarineLifeSuggestion = (suggestion) => {
    const current = formData.marine_life_observations;
    const observations = current ? current.split(',').map(s => s.trim()) : [];
    if (!observations.includes(suggestion)) {
      observations.push(suggestion);
      setFormData(prev => ({
        ...prev,
        marine_life_observations: observations.join(', ')
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.dive_site_id) {
      toast.error("Please select a dive site");
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      const observations = formData.marine_life_observations
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);
      
      await axios.post(
        `${API}/dive-site-reports`,
        {
          user_id: user.id,
          dive_site_id: formData.dive_site_id,
          rating: formData.rating,
          marine_life_observations: observations,
          image_urls: formData.image_urls
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success("Dive site report submitted successfully!");
      
      // Redirect to profile after short delay
      setTimeout(() => {
        navigate(`/profile/${user.id}`);
      }, 2000);
    } catch (error) {
      toast.error("Failed to submit report. Please try again.");
      console.error("Error submitting report:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900">
      <Navigation />

      <div className="container mx-auto px-4 pt-32 pb-12 max-w-3xl">
        <Card className="bg-white/5 backdrop-blur-md border-white/10 text-white">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                <MapPin className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl mb-4">Submit Dive Site Report</CardTitle>
            <CardDescription className="text-gray-300 text-lg">
              Share your observations and help build our marine life database
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="dive_site" className="text-white">Select Dive Site *</Label>
                <Select
                  value={formData.dive_site_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, dive_site_id: value }))}
                  required
                >
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Choose a dive site..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-white/20 max-h-[300px] overflow-y-auto">
                    {diveSites.map((site) => (
                      <SelectItem key={site.id} value={site.id} className="text-white hover:bg-white/10">
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.dive_site_id && (
                <>
                  <div className="space-y-2">
                    <Label className="text-white">Dive Site Rating *</Label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleRatingChange(star)}
                          className="transition-transform hover:scale-110"
                        >
                          <Star
                            className={`w-10 h-10 ${
                              star <= formData.rating
                                ? "fill-yellow-400 text-yellow-400"
                                : "text-gray-400 hover:text-yellow-300"
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="marine_life" className="text-white">Marine Life Observations</Label>
                    <Textarea
                      id="marine_life"
                      value={formData.marine_life_observations}
                      onChange={(e) => setFormData(prev => ({ ...prev, marine_life_observations: e.target.value }))}
                      placeholder="Enter marine life separated by commas (e.g., Sea Turtle, Clownfish, Reef Shark)"
                      rows={4}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 resize-none"
                    />
                    
                    {marineLifeSuggestions.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-400 mb-2">💡 Common sightings at this site:</p>
                        <div className="flex flex-wrap gap-2">
                          {marineLifeSuggestions.slice(0, 15).map((suggestion, idx) => {
                            const fontSize = Math.min(18, 10 + suggestion.frequency);
                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => addMarineLifeSuggestion(suggestion.name)}
                                className="px-3 py-1 bg-cyan-500/20 border border-cyan-500/30 rounded-full text-cyan-200 hover:bg-cyan-500/30 transition-colors"
                                style={{ fontSize: `${fontSize}px` }}
                              >
                                {suggestion.name} ({suggestion.frequency})
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <MultiImageUpload
                      images={formData.image_urls}
                      onImagesChange={(urls) => setFormData(prev => ({...prev, image_urls: urls}))}
                      uploadEndpoint="/api/upload/report-image"
                      maxImages={5}
                      label="Add Photos (Optional)"
                    />
                  </div>
                </>
              )}

              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                <p className="text-cyan-200 text-sm">
                  <strong>Note:</strong> Your observations will be added to this dive site&apos;s marine life database and help other divers know what to expect.
                </p>
              </div>
            </CardContent>
            
            <CardFooter>
              <Button
                type="submit"
                disabled={loading || !formData.dive_site_id}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white text-lg py-6"
              >
                {loading ? "Submitting..." : "Submit Dive Site Report"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
      
      <FloatingUserMenu />
      <Footer />

    </div>
  );
};

export default SubmitDiveSiteReport;
